// Central UTM tagging for every marketing -> app hand-off on the free tools.
//
// Signup attribution already flows end to end on its own: captureFirstTouchAttribution
// stores utm_source/medium/campaign/content in a cookie shared across both origins,
// and every track() call — including signup_complete — merges it in. The only thing
// that was missing was tool pages writing values distinct enough to tell 40 tools
// apart, so all of them collapsed into a single utm_medium=tool bucket.
//
// The scheme mirrors what the blog already does (see BlogPost.tsx):
//   utm_source   = marketing      (stable: which property)
//   utm_medium   = tool           (stable: which surface type — groupable)
//   utm_campaign = <tool slug>    (which specific tool)
//   utm_content  = <placement>    (which CTA on that tool's page)
//
// Keep this free of browser globals: it is imported by the SSR bundle and by the
// Node prerender/sitemap scripts.

/** Where on a tool page the hand-off was triggered. */
export type ToolPlacement =
  | "hero_cta"
  | "save_to_canvas"
  | "widget_cta"
  | "index_cta"
  | "save_to_canvas_fallback";

const APP_ORIGIN = "https://app.omnicanvasnotes.com";

/**
 * Force the four attribution params onto an app URL, preserving every other
 * param the caller set (mode=try, action=new-note, template=..., import=...).
 *
 * Existing utm values are overwritten rather than merged: the callers that had
 * hand-rolled tags were exactly the ones producing ambiguous data.
 */
export function withToolAttribution(
  href: string,
  campaign: string,
  placement: ToolPlacement
): string {
  let url: URL;
  try {
    // Parsed against the app origin, so a relative href resolves there rather
    // than throwing. In practice this makes the catch below unreachable for
    // anything callers actually pass; it stays as a guard so a bad registry
    // entry can never cost a click.
    url = new URL(href, APP_ORIGIN);
  } catch {
    return href;
  }
  url.searchParams.set("utm_source", "marketing");
  url.searchParams.set("utm_medium", "tool");
  url.searchParams.set("utm_campaign", campaign);
  url.searchParams.set("utm_content", placement);
  return url.toString();
}

/**
 * The default "try it" destination for a tool, used when a tool has no bespoke
 * appHref of its own.
 */
export function toolAppHref(slug: string, placement: ToolPlacement): string {
  return withToolAttribution(
    `${APP_ORIGIN}/?mode=try&action=new-note`,
    slug,
    placement
  );
}
